import React, { createContext, useContext, useState, useEffect } from 'react';

import apiClient from '../services/apiClient';

interface Task {
  id: string;
  title: string;
  description?: string;
  status: 'todo' | 'in_progress' | 'completed';
  priority: 'low' | 'medium' | 'high';
  dueDate?: string;
  assignedTo?: string;
  caseId?: string;
  clientId?: string;
  createdAt: string;
  updatedAt: string;
  tags?: string[];
  completedAt?: string;
}

interface TasksContextType {
  tasks: Task[];
  loading: boolean;
  error: string | null;
  fetchTasks: () => Promise<void>;
  addTask: (taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateTask: (id: string, taskData: Partial<Task>) => Promise<void>;
  deleteTask: (id: string) => Promise<void>;
  getTaskById: (id: string) => Task | undefined;
  getTasksByCase: (caseId: string) => Task[];
  getTasksByClient: (clientId: string) => Task[];
  getTasksByStatus: (status: Task['status']) => Task[];
  getTasksByPriority: (priority: Task['priority']) => Task[];
  getTasksByAssignee: (assigneeId: string) => Task[];
}

const TasksContext = createContext<TasksContextType | undefined>(undefined);

export const useTasks = () => {
  const context = useContext(TasksContext);
  if (context === undefined) {
    throw new Error('useTasks must be used within a TasksProvider');
  }
  return context;
};

export const TasksProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/tasks');
      setTasks(res.data || res);
      setError(null);
    } catch (err) {
      setError('Failed to fetch tasks');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const addTask = async (taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => {
    setLoading(true);
    try {
      const newTask = await apiClient.post('/api/tasks', taskData);
      setTasks(prev => [...prev, newTask]);
      setError(null);
    } catch (err) {
      setError('Failed to add task');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateTask = async (id: string, taskData: Partial<Task>) => {
    setLoading(true);
    try {
      const updatedTask = await apiClient.patch(`/api/tasks/${id}`, taskData);
      setTasks(prev => prev.map(t => (t.id === id ? updatedTask : t)));
      setError(null);
    } catch (err) {
      setError('Failed to update task');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const deleteTask = async (id: string) => {
    setLoading(true);
    try {
      await apiClient.delete(`/api/tasks/${id}`);
      setTasks(prev => prev.filter(t => t.id !== id));
      setError(null);
    } catch (err) {
      setError('Failed to delete task');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getTaskById = (id: string) => {
    return tasks.find(t => t.id === id);
  };

  const getTasksByCase = (caseId: string) => {
    return tasks.filter(t => t.caseId === caseId);
  };

  const getTasksByClient = (clientId: string) => {
    return tasks.filter(t => t.clientId === clientId);
  };

  const getTasksByStatus = (status: Task['status']) => {
    return tasks.filter(t => t.status === status);
  };

  const getTasksByPriority = (priority: Task['priority']) => {
    return tasks.filter(t => t.priority === priority);
  };

  const getTasksByAssignee = (assigneeId: string) => {
    return tasks.filter(t => t.assignedTo === assigneeId);
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const value = {
    tasks,
    loading,
    error,
    fetchTasks,
    addTask,
    updateTask,
    deleteTask,
    getTaskById,
    getTasksByCase,
    getTasksByClient,
    getTasksByStatus,
    getTasksByPriority,
    getTasksByAssignee,
  };

  return <TasksContext.Provider value={value}>{children}</TasksContext.Provider>;
};
